import React, { useEffect, useRef } from "react";
import {
  ApplicationFeature,
  DoubleMaterialityValueChainPart,
  EntityType,
  FileMetaDataDto,
  UploadedFileFeature,
} from "@green0meter/api-client";
import type { UseFormReturn } from "react-hook-form";
import { Close as CloseIcon } from "@mui/icons-material";
import FeatherIcon from "feather-icons-react";
import type { AssessmentDto } from "@green0meter/api-client";

import RichTextControllerField from "./components/RichTextControllerField";
import {
  renderTopicSelect,
  renderSubTopicSelect,
  renderSubSubTopicSelect,
  useValueChainLabels,
} from "./AssessmentEditor.utils";
import type { AssessmentFormValues } from "./AssessmentEditor.types";

import {
  Chip,
  Box,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Button,
} from "@components/base";
import LabeledControllerField from "@components/common/LabeledControllerField/LabeledControllerField";
import { useGetMasterRelatedCompanies } from "@api/hooks/companies/GetMasterRelatedCompanies/useGetMasterRelatedCompanies";
import {
  useUploadFile,
  type UploadedFileInfoModel,
} from "@api/hooks/fileStorage/UploadFile/useUploadFile";
import { useUserStore } from "@store/user";
import { FileListItem } from "@components/common";

type Props = {
  form: UseFormReturn<AssessmentFormValues>;
  disabled: boolean;
  t: (key: string, defaultValue: string) => string;
  assessment: AssessmentDto | null;
};

const AssessmentEditorContent: React.FC<Props> = ({
  form,
  disabled,
  t,
  assessment,
}) => {
  const { control, watch, setValue } = form;
  const { getValueChainLabel } = useValueChainLabels();
  const { user } = useUserStore();
  const selectedTopic = watch("topic");
  const selectedSubTopic = watch("subTopic");
  const attachments = watch("attachments");

  // Fetch child companies
  const { masterRelatedCompanies: relatedCompanies } =
    useGetMasterRelatedCompanies(user);

  const previousTopicRef = useRef<typeof selectedTopic>();
  const previousSubTopicRef = useRef<typeof selectedSubTopic>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFileAsync, uploadFileEnabled, isPendingUploadFile } =
    useUploadFile(user);

  useEffect(() => {
    if (
      previousTopicRef.current !== undefined &&
      previousTopicRef.current !== selectedTopic
    ) {
      setValue("subTopic", null);
      setValue("subSubTopic", null);
    }

    previousTopicRef.current = selectedTopic;
  }, [selectedTopic, setValue]);

  useEffect(() => {
    if (
      previousSubTopicRef.current !== undefined &&
      previousSubTopicRef.current !== selectedSubTopic
    ) {
      setValue("subSubTopic", null);
    }

    previousSubTopicRef.current = selectedSubTopic;
  }, [selectedSubTopic, setValue]);

  const mapUploadedFileToMeta = (
    file: UploadedFileInfoModel,
    entityId: string,
  ): FileMetaDataDto => ({
    fileId: file.fileId,
    name: file.originalName,
    size: file.fileSize,
    mimeType: file.fileType,
    pathName: file.path,
    databaseEntityId: entityId,
    databaseEntityType: EntityType.DoubleMateriality,
    createdAt: new Date().toISOString(),
    updatedAt: null,
    relatedFeature: ApplicationFeature.CSRD,
  });

  const handleUploadClick = (): void => {
    if (disabled || !uploadFileEnabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = event.target.files;
    const entityId =
      assessment?.id || assessment?.assessmentId || watch("assessmentId");

    if (!files || !uploadFileEnabled || !entityId) {
      event.target.value = "";

      return;
    }

    try {
      const uploadedFiles = await Promise.all(
        Array.from(files).map((file) =>
          uploadFileAsync({
            file,
            entityId,
            databaseEntityType: EntityType.DoubleMateriality,
            uploadedFileFeature: UploadedFileFeature.FormData,
          }),
        ),
      );

      const mappedAttachments = uploadedFiles.map((file) =>
        mapUploadedFileToMeta(file, entityId),
      );

      setValue("attachments", [...(attachments || []), ...mappedAttachments], {
        shouldDirty: true,
        shouldTouch: true,
      });
    } catch (error) {
      console.error("Failed to upload assessment attachments", error);
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoveAttachment = async (file: FileMetaDataDto): Promise<void> => {
    const identifier = file.fileId ?? file.pathName;
    const remainingAttachments =
      attachments?.filter((item) => {
        const itemId = item.fileId ?? item.pathName;

        return itemId !== identifier;
      }) || [];

    setValue("attachments", remainingAttachments, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Stack spacing={2} sx={{ flex: 1 }}>
        <LabeledControllerField
          label={t("assessments.fields.topic", "Topic")}
          name="topic"
          control={control}
          disabled={disabled}
          render={({ field, error }) => renderTopicSelect(field, error, disabled)}
        />

        <LabeledControllerField
          label={t("assessments.fields.subTopic", "Sub-topic")}
          name="subTopic"
          control={control}
          disabled={disabled}
          render={({ field, error }) =>
            renderSubTopicSelect(field, error, disabled, selectedTopic)
          }
        />

        <LabeledControllerField
          label={t("assessments.fields.subSubTopic", "Sub-sub-topic")}
          name="subSubTopic"
          control={control}
          disabled={disabled}
          render={({ field, error }) =>
            renderSubSubTopicSelect(
              field,
              error,
              disabled,
              selectedTopic,
              selectedSubTopic,
            )
          }
        />

        <LabeledControllerField
          label={t("assessments.fields.year", "Year")}
          name="year"
          control={control}
          disabled={disabled}
          render={({ field, error }) => (
            <TextField
              {...field}
              type="number"
              error={Boolean(error)}
              helperText={error?.message}
              disabled={disabled}
              fullWidth
              variant="filled"
            />
          )}
        />

        <LabeledControllerField
          label={t("assessments.fields.id", "Assessment ID")}
          name="assessmentId"
          control={control}
          disabled={disabled}
          render={({ field, error }) => (
            <TextField
              {...field}
              error={Boolean(error)}
              helperText={error?.message}
              disabled={disabled}
              InputProps={{ readOnly: false }}
              placeholder={
                !assessment ? "Auto-generated based on selections..." : ""
              }
              fullWidth
              variant="filled"
            />
          )}
        />

        <LabeledControllerField
          label={t("assessments.fields.partOfValueChain", "Part of the value chain")}
          name="partOfValueChain"
          control={control}
          disabled={disabled}
          render={({ field, error }) => (
            <FormControl fullWidth error={Boolean(error)}>
              <Select
                {...field}
                multiple
                disabled={disabled}
                variant="filled"
                fullWidth
                sx={{
                  "& .MuiSelect-select": {
                    width: "100%",
                    height: "100%",
                    minHeight: "inherit",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiSelect-icon": { pointerEvents: "none" },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as DoubleMaterialityValueChainPart[]).map((value) => (
                      <Chip
                        key={value}
                        label={getValueChainLabel(value)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {(
                  Object.values(
                    DoubleMaterialityValueChainPart,
                  ) as DoubleMaterialityValueChainPart[]
                ).map((part) => (
                  <MenuItem
                    key={part as DoubleMaterialityValueChainPart}
                    value={part as DoubleMaterialityValueChainPart}
                  >
                    {getValueChainLabel(part as DoubleMaterialityValueChainPart)}
                  </MenuItem>
                ))}
              </Select>
              {error && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />

        <LabeledControllerField
          label={t("assessments.fields.relevantSubjects", "Relevant subjects")}
          name="relevantSubjects"
          control={control}
          disabled={disabled}
          render={({ field, error }) => (
            <FormControl fullWidth error={Boolean(error)}>
              <Select
                {...field}
                multiple
                disabled={disabled}
                variant="filled"
                fullWidth
                sx={{
                  "& .MuiSelect-select": {
                    width: "100%",
                    height: "100%",
                    minHeight: "inherit",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiSelect-icon": { pointerEvents: "none" },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {(selected as string[]).map((companyId) => {
                      const company = relatedCompanies?.find(
                        (c) => c.id === companyId,
                      );

                      return (
                        <Chip
                          key={companyId}
                          label={company?.name || companyId}
                          size="small"
                          sx={{
                            m: 0.5,
                            "& .MuiChip-label": {
                              px: 1.5,
                              py: 0.5,
                            },
                          }}
                          deleteIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                          onDelete={() => {
                            const newValue = (selected as string[]).filter(
                              (id) => id !== companyId,
                            );

                            field.onChange(newValue);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {relatedCompanies?.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
              {error && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />

        <LabeledControllerField
          label={t(
            "assessments.fields.sustainabilityMatter",
            "Sustainability matter",
          )}
          name="sustainabilityMatter"
          control={control}
          disabled={disabled}
          render={({ field, error }) => (
            <TextField
              {...field}
              multiline
              rows={3}
              error={Boolean(error)}
              helperText={error?.message}
              disabled={disabled}
              fullWidth
              variant="filled"
            />
          )}
        />

        <RichTextControllerField
          label={t("assessments.fields.impacts", "Impact")}
          name="impacts"
          control={control}
          disabled={disabled}
          placeholder={t("assessments.placeholders.impacts", "Type Impacts here")}
        />

        <RichTextControllerField
          label={t("assessments.fields.risks", "Risks")}
          name="risks"
          control={control}
          disabled={disabled}
          placeholder={t("assessments.placeholders.risks", "Type Risks here")}
        />

        <RichTextControllerField
          label={t("assessments.fields.opportunities", "Opportunities")}
          name="opportunities"
          control={control}
          disabled={disabled}
          placeholder={t(
            "assessments.placeholders.opportunities",
            "Type Opportunities here",
          )}
        />

        <RichTextControllerField
          label={t("assessments.fields.sources", "Sources")}
          name="sources"
          control={control}
          disabled={disabled}
          placeholder={t("assessments.placeholders.sources", "Type Sources here")}
        />

        <Stack spacing={1}>
          <Typography variant="subtitle2">
            {t("assessments.fields.attachments", "Attachments")}
          </Typography>

          {attachments?.length ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {attachments.map((file) => (
                <FileListItem
                  key={file.fileId ?? file.pathName ?? file.name}
                  file={{
                    ...file,
                    fileId: file.fileId ?? null,
                    databaseEntityType:
                      file.databaseEntityType ?? EntityType.DoubleMateriality,
                  }}
                  onDelete={() => handleRemoveAttachment(file)}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("assessments.messages.noAttachments", "No files attached yet.")}
            </Typography>
          )}

          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            onChange={handleFileChange}
            disabled={disabled || !uploadFileEnabled}
          />

          <Button
            variant="outlined"
            startIcon={<FeatherIcon icon="paperclip" size={16} />}
            onClick={handleUploadClick}
            disabled={disabled || !uploadFileEnabled}
            isLoading={isPendingUploadFile}
          >
            {t("assessments.actions.addAttachment", "Upload files")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AssessmentEditorContent;
